import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import {
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import Header from '../../components/Header';
import IconsBar from '../../components/IconsBar/IconsBar';
import { Loading } from '../../components/Loading/Loading';
import { AlertMessages } from '../../components/AlertMessages';
import { AuthContext } from '../../contexts/Auth/AuthContext';
import {
  getProducts,
  createOrder,
  getBalance,
  getCardInfo,
  getWalletStudentsPaginated,
  getWalletEmployeesPaginated,
} from '../../api/endpoints/wallet';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatCurrency, toCents, fromCents } from '../../utils/formatCoins';
import {
  Container,
  ContentArea,
  PageTitle,
  PDVArea,
  ProductsArea,
  CartArea,
  CartItems,
  CartItem,
  CartTotal,
  ProductGrid,
  ProductCard,
  ProductName,
  ProductPrice,
} from './styles';

const Canteen = () => {
  const { user } = useContext(AuthContext);
  const [studentCode, setStudentCode] = useState('');
  const [studentName, setStudentName] = useState('');
  const [studentBalance, setStudentBalance] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [cart, setCart] = useState([]);
  const [nfcReading, setNfcReading] = useState(false);
  const [identifyModalOpen, setIdentifyModalOpen] = useState(false);
  const [modalCodeInput, setModalCodeInput] = useState(''); // Código digitado no modal (mascarado)
  const [quickSearchOpen, setQuickSearchOpen] = useState(false);
  const [quickSearchTerm, setQuickSearchTerm] = useState('');
  const [quickSearchResults, setQuickSearchResults] = useState([]);
  const [quickSearchLoading, setQuickSearchLoading] = useState(false);
  const [quickSearchError, setQuickSearchError] = useState('');
  const [quickSearchHighlightedIndex, setQuickSearchHighlightedIndex] = useState(-1);
  // Carregar filial do localStorage, ou usar ALTO_BRANCO como padrão apenas na primeira vez
  const [branch, setBranch] = useState(() => {
    try {
      const savedBranch = localStorage.getItem('canteen_filial');
      if (savedBranch && (savedBranch === 'ALTO_BRANCO' || savedBranch === 'CONCEICAO')) {
        return savedBranch;
      }
    } catch (error) {
      console.error('[Canteen] Erro ao carregar filial do localStorage:', error);
    }
    // Padrão apenas na primeira vez - depois sempre salvará no localStorage
    const defaultBranch = 'ALTO_BRANCO';
    // Salvar padrão no localStorage na primeira vez
    try {
      localStorage.setItem('canteen_filial', defaultBranch);
    } catch (error) {
      console.error('[Canteen] Erro ao salvar filial padrão:', error);
    }
    return defaultBranch;
  });
  const lastSearchedCodeRef = useRef('');
  const searchTimerRef = useRef(null);
  const isInitializingRef = useRef(false); // Flag para bloquear eventos durante inicialização
  const inputRef = useRef(null); // Ref para o input do código
  const quickSearchInputRef = useRef(null);
  const quickSearchTimerRef = useRef(null);
  const quickSearchRequestRef = useRef(0);
  const filteredProductsRef = useRef([]);
  const identifyModalOpenRef = useRef(false);
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenRef = useRef(null);
  const userWantsFullscreenRef = useRef(false); // true = usuário entrou por F1; ESC não deve sair

  // Garantir que a filial padrão seja salva no localStorage na primeira vez e sincronizar estado
  useEffect(() => {
    try {
      const savedBranch = localStorage.getItem('canteen_filial');
      if (!savedBranch || (savedBranch !== 'ALTO_BRANCO' && savedBranch !== 'CONCEICAO')) {
        // Se não existe ou é inválida, salvar a padrão
        const defaultBranch = branch || 'ALTO_BRANCO';
        localStorage.setItem('canteen_filial', defaultBranch);
        if (branch !== defaultBranch) {
          setBranch(defaultBranch);
        }
      } else if (savedBranch !== branch) {
        // Se localStorage tem valor diferente do estado, sincronizar
        setBranch(savedBranch);
      }
    } catch (error) {
      console.error('[Canteen] Erro ao salvar filial padrão no localStorage:', error);
    }
  }, []); // Apenas na montagem

  // Carregar produtos ao montar e quando branch mudar
  useEffect(() => {
    loadProducts();
  }, [branch]);

  // Gerenciar modo tela cheia (sincronizar estado; não reentrar por ESC — navegador bloqueia e gera erro)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
      if (!isCurrentlyFullscreen) {
        userWantsFullscreenRef.current = false;
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const handleToggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        userWantsFullscreenRef.current = true;
        // Entrar em tela cheia
        const element = fullscreenRef.current || document.documentElement;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
      } else {
        userWantsFullscreenRef.current = false;
        // Sair de tela cheia
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Erro ao alternar modo tela cheia:', error);
      setAlertState({
        show: true,
        type: 'error',
        message: 'Não foi possível alternar o modo tela cheia',
      });
    }
  };

  // Abrir modal de leitura do cartão (reutilizado pelo botão e pelo atalho F2)
  const openCardModal = useCallback(() => {
    setProductSearch('');
    setModalCodeInput('');
    setQuickSearchOpen(false);
    setQuickSearchTerm('');
    setQuickSearchResults([]);
    setQuickSearchError('');
    setQuickSearchHighlightedIndex(-1);
    lastSearchedCodeRef.current = '';
    setTimeout(() => {
      const productInput = document.querySelector('input[placeholder="Buscar produto..."]');
      if (productInput) {
        productInput.value = '';
        setProductSearch('');
      }
    }, 0);
    setIdentifyModalOpen(true);
  }, []);

  const openQuickSearch = useCallback(() => {
    setQuickSearchOpen(true);
    setQuickSearchTerm('');
    setQuickSearchResults([]);
    setQuickSearchError('');
    setQuickSearchHighlightedIndex(-1);
    setTimeout(() => quickSearchInputRef.current?.focus(), 0);
  }, []);

  const closeQuickSearch = useCallback(() => {
    setQuickSearchOpen(false);
    setQuickSearchTerm('');
    setQuickSearchResults([]);
    setQuickSearchError('');
    setQuickSearchHighlightedIndex(-1);
    if (quickSearchTimerRef.current) {
      clearTimeout(quickSearchTimerRef.current);
      quickSearchTimerRef.current = null;
    }
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  // Prevenir que o navegador tente salvar senhas quando NFC detectar código
  useEffect(() => {
    const preventPasswordSave = () => {
      // Remover qualquer formulário que o navegador possa estar tentando salvar
      const forms = document.querySelectorAll('form');
      forms.forEach(form => {
        form.setAttribute('autocomplete', 'off');
        form.setAttribute('data-lpignore', 'true');
        form.setAttribute('data-form-type', 'other');
      });
      
      // Garantir que campos de código de estudante não sejam tratados como campos de login
      const studentInputs = document.querySelectorAll('input[name*="student"], input[name*="code"], input[placeholder*="cartão"], input[placeholder*="código"]');
      studentInputs.forEach(input => {
        input.setAttribute('autocomplete', 'off');
        input.setAttribute('data-lpignore', 'true');
        input.setAttribute('data-form-type', 'other');
        input.setAttribute('data-1p-ignore', 'true');
        // Garantir que não seja tipo password
        if (input.type === 'password') {
          input.type = 'text';
        }
      });
      
      // Garantir que campos de pesquisa não sejam tratados como campos de login
      const searchInputs = document.querySelectorAll('input[type="search"], input[placeholder*="Buscar"]');
      searchInputs.forEach(input => {
        input.setAttribute('autocomplete', 'off');
        input.setAttribute('data-lpignore', 'true');
        input.setAttribute('data-form-type', 'other');
        input.setAttribute('data-1p-ignore', 'true');
        // Se vier preenchido com "ivanildo" ou similar, limpar
        if (input.value && (input.value.toLowerCase().includes('ivanildo') || input.value.length < 3)) {
          input.value = '';
          if (input.name === 'product-search-canteen') {
            setProductSearch('');
          }
        }
      });
      
      // Prevenir que o Dialog seja interpretado como formulário de login
      const dialogs = document.querySelectorAll('[role="dialog"]');
      dialogs.forEach(dialog => {
        dialog.setAttribute('data-lpignore', 'true');
        dialog.setAttribute('data-form-type', 'other');
      });
    };
    
    // Executar quando modal abrir ou quando estudante for identificado
    if (identifyModalOpen || studentBalance !== null) {
      preventPasswordSave();
      const interval = setInterval(preventPasswordSave, 100);
      return () => clearInterval(interval);
    }
  }, [identifyModalOpen, studentBalance]);

  // Filtrar produtos quando busca ou lista mudar
  useEffect(() => {
    if (!productSearch.trim()) {
      setFilteredProducts(products);
    } else {
      const searchLower = productSearch.toLowerCase();
      setFilteredProducts(
        products.filter(
          (product) =>
            product.name.toLowerCase().includes(searchLower) ||
            (product.sku && product.sku.toLowerCase().includes(searchLower))
        )
      );
    }
  }, [productSearch, products]);

  // Monitorar e limpar campo de pesquisa se vier preenchido pelo autofill
  useEffect(() => {
    const checkAndCleanProductSearch = () => {
      const productInput = document.querySelector('input[name="product-search-canteen"]');
      if (productInput && productInput.value && productInput.value !== productSearch) {
        // Campo foi preenchido pelo autofill - limpar
        productInput.value = '';
        setProductSearch('');
        // Remover foco para evitar que o navegador tente salvar
        productInput.blur();
      }
    };
    
    // Verificar periodicamente quando o modal estiver aberto ou quando estudante for identificado
    const interval = setInterval(checkAndCleanProductSearch, 100);
    
    return () => clearInterval(interval);
  }, [productSearch, identifyModalOpen, studentBalance]);

  // Limpar campos do modal quando o modal abrir
  useEffect(() => {
    if (identifyModalOpen) {
      // Marcar como inicializando para bloquear eventos
      isInitializingRef.current = true;
      
      // SEMPRE limpar tudo quando o modal abrir
      const cleanup = () => {
        setModalCodeInput('');
        // NÃO limpar studentCode aqui - ele será definido quando estudante for encontrado
        // Limpar campo de pesquisa também (pode estar sendo preenchido pelo autofill)
        setProductSearch('');
        
        // Limpar referência de busca para permitir nova busca
        lastSearchedCodeRef.current = '';
        // Limpar timer se existir
        if (searchTimerRef.current) {
          clearTimeout(searchTimerRef.current);
          searchTimerRef.current = null;
        }
        // Resetar loading states
        setNfcReading(false);
        // NÃO limpar estados de estudante aqui - manter se já existir
        
        // Limpar o campo do DOM diretamente (para contornar autofill)
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        
        // Limpar campo de pesquisa do DOM também e remover foco
        const productInput = document.querySelector('input[name="product-search-canteen"]');
        if (productInput) {
          productInput.value = '';
          productInput.blur();
          // Prevenir autofill
          productInput.setAttribute('autocomplete', 'off');
          productInput.setAttribute('data-lpignore', 'true');
        }
      };
      
      // Limpar imediatamente
      cleanup();
      // Limpar novamente após delays para garantir (caso autofill tente preencher)
      const timeout1 = setTimeout(cleanup, 50);
      const timeout2 = setTimeout(cleanup, 150);
      const timeout3 = setTimeout(() => {
        cleanup();
        // Liberar bloqueio após 500ms
        setTimeout(() => {
          isInitializingRef.current = false;
        }, 100);
      }, 500);
      
      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
        isInitializingRef.current = false;
      };
    } else {
      // Quando o modal fechar, apenas limpar o campo mascarado do modal
      // NÃO limpar studentCode, studentBalance, studentName - manter dados do estudante
      isInitializingRef.current = false;
      setModalCodeInput('');
      // Limpar timers
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
    }
  }, [identifyModalOpen]);

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      const data = await getProducts(branch); // Passar branch ao carregar produtos
      setProducts(data.products || []);
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setProductsLoading(false);
    }
  };

  const handleSearchStudent = useCallback(async (codeToSearch = null) => {
    const code = codeToSearch || studentCode.trim();
    
    if (!code) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Informe o código do cartão (estudante ou colaborador)',
      });
      return;
    }

    const normalizedCode = code.toUpperCase();
    
    // Prevenir busca duplicada - se já está buscando o mesmo código, não tentar novamente
    if (normalizedCode === lastSearchedCodeRef.current && loading) {
      return; // Já está buscando este código
    }

    try {
      setLoading(true);
      setNfcReading(true);
      
      // Marcar como buscado ANTES da requisição para evitar duplicatas
      lastSearchedCodeRef.current = normalizedCode;
      
      // Buscar como estudante ou colaborador
      const data = await getCardInfo(normalizedCode);
      
      // Configurar estado baseado no tipo (estudante ou colaborador)
      setStudentName(data.name || '');
      setStudentCode(normalizedCode);
      
      if (data.ownerType === 'STUDENT') {
        // Estudante: tem saldo
        const balanceValue = typeof data.balance === 'string' 
          ? parseFloat(data.balance) 
          : Number(data.balance);
        setStudentBalance(isNaN(balanceValue) ? 0 : balanceValue);
      } else {
        // Colaborador: sem saldo
        setStudentBalance(null);
      }
      
      // Limpar campo mascarado do modal por segurança
      setModalCodeInput('');
      
      // LIMPAR campo de pesquisa quando cartão for identificado (evita autofill)
      setProductSearch('');
      const productInput = document.querySelector('input[name="product-search-canteen"]');
      if (productInput) {
        productInput.value = '';
        productInput.blur();
      }
      
      // Fechar modal se estiver aberto (usar forma funcional para não precisar da dependência)
      setIdentifyModalOpen((prev) => prev ? false : prev);
      
      // Feedback visual de sucesso
      if (codeToSearch) {
        // Se foi chamado automaticamente (NFC), mostrar feedback
        const ownerLabel = data.ownerType === 'EMPLOYEE' ? 'Colaborador' : 'Estudante';
        setAlertState({
          show: true,
          type: 'success',
          message: `${ownerLabel} encontrado: ${data.name || normalizedCode}`,
        });
      }
    } catch (error) {
      // Limpar estado do estudante
      setStudentBalance(null);
      setStudentName('');
      setStudentCode(''); // Limpar código para evitar loop - não tentar buscar novamente automaticamente
      
      // Limpar campo mascarado do modal
      setModalCodeInput('');
      
      // Manter o lastSearchedCodeRef com o código que falhou para evitar nova busca automática
      // Isso previne loop infinito: mesmo que algo tente buscar novamente, já está marcado como buscado
      // Será limpo quando o modal fechar ou quando o usuário digitar um código diferente
      
      // Mostrar erro apenas uma vez (sempre mostrar para códigos completos)
      const currentCode = codeToSearch || normalizedCode;
      if (!codeToSearch || currentCode.length >= 8) {
        setAlertState({
          show: true,
          type: 'error',
          message: getErrorMessage(error),
        });
      }
    } finally {
      setLoading(false);
      setNfcReading(false);
    }
  }, [studentCode, loading]);

  const handleQuickPersonSearch = async (termToSearch = quickSearchTerm) => {
    const term = termToSearch.trim();
    if (term.length < 3) {
      setQuickSearchError('Digite pelo menos 3 letras do nome.');
      setQuickSearchResults([]);
      setQuickSearchHighlightedIndex(-1);
      return;
    }

    const requestId = quickSearchRequestRef.current + 1;
    quickSearchRequestRef.current = requestId;

    try {
      setQuickSearchLoading(true);
      setQuickSearchError('');
      const searchParams = {
        page: 1,
        pageSize: 10,
        search: term,
        status: 'active',
      };
      const [studentResult, employeeResult] = await Promise.allSettled([
        getWalletStudentsPaginated(searchParams),
        getWalletEmployeesPaginated(searchParams),
      ]);

      if (studentResult.status === 'rejected' && employeeResult.status === 'rejected') {
        throw studentResult.reason;
      }

      const students = studentResult.status === 'fulfilled'
        ? (studentResult.value.students || []).map((student) => ({
            ...student,
            cardCode: student.studentCode,
            ownerType: 'STUDENT',
          }))
        : [];
      const employees = employeeResult.status === 'fulfilled'
        ? (employeeResult.value.employees || []).map((employee) => ({
            ...employee,
            cardCode: employee.employeeCode,
            ownerType: 'EMPLOYEE',
          }))
        : [];
      const matches = [...students, ...employees]
        .sort((first, second) => String(first.name || '').localeCompare(String(second.name || ''), 'pt-BR'));

      if (requestId !== quickSearchRequestRef.current) return;
      setQuickSearchResults(matches);
      setQuickSearchHighlightedIndex(matches.length > 0 ? 0 : -1);
      if (matches.length === 0) {
        setQuickSearchError('Nenhum aluno ou colaborador ativo com cartão NFC foi encontrado.');
      }
    } catch (error) {
      if (requestId !== quickSearchRequestRef.current) return;
      setQuickSearchResults([]);
      setQuickSearchHighlightedIndex(-1);
      setQuickSearchError(getErrorMessage(error));
    } finally {
      if (requestId === quickSearchRequestRef.current) {
        setQuickSearchLoading(false);
      }
    }
  };

  const scheduleQuickPersonSearch = (value) => {
    if (quickSearchTimerRef.current) {
      clearTimeout(quickSearchTimerRef.current);
    }
    // Invalida qualquer resposta de uma busca anterior ainda em andamento.
    quickSearchRequestRef.current += 1;

    const term = value.trim();
    if (term.length < 3) {
      setQuickSearchLoading(false);
      setQuickSearchResults([]);
      setQuickSearchHighlightedIndex(-1);
      setQuickSearchError('');
      return;
    }

    setQuickSearchLoading(true);
    quickSearchTimerRef.current = setTimeout(() => {
      handleQuickPersonSearch(value);
    }, 400);
  };

  const moveQuickSearchHighlight = (direction) => {
    if (quickSearchResults.length === 0) return;
    setQuickSearchHighlightedIndex((currentIndex) => {
      const nextIndex = currentIndex < 0
        ? 0
        : (currentIndex + direction + quickSearchResults.length) % quickSearchResults.length;
      setTimeout(() => {
        document.querySelector(`[data-quick-search-index="${nextIndex}"]`)?.scrollIntoView({
          block: 'nearest',
        });
      }, 0);
      return nextIndex;
    });
  };

  const handleSelectQuickSearchPerson = (person) => {
    const code = String(person?.cardCode || '').trim().toUpperCase();
    if (!code) {
      setQuickSearchError('Este cadastro não possui código de cartão NFC.');
      return;
    }

    setModalCodeInput(code);
    setStudentCode(code);
    lastSearchedCodeRef.current = '';
    setQuickSearchOpen(false);
    setQuickSearchTerm('');
    setQuickSearchResults([]);
    setQuickSearchError('');
    setQuickSearchHighlightedIndex(-1);
    handleSearchStudent(code);
  };

  useEffect(() => () => {
    if (quickSearchTimerRef.current) {
      clearTimeout(quickSearchTimerRef.current);
    }
  }, []);

  // Auto-buscar estudante quando código completo for detectado (para NFC)
  // NÃO buscar se o modal estiver fechado (evita buscas indesejadas)
  useEffect(() => {
    // Não buscar se o modal não estiver aberto
    if (!identifyModalOpen) {
      return;
    }
    
    const code = studentCode.trim();
    // Aceitar códigos com 8+ caracteres (com ou sem hífen)
    // Formatos aceitos: "C3F9952F" (8 chars) ou "12345678-6793" (com hífen)
    const hasCompleteFormat = code.length >= 8;
    
    // Só buscar se:
    // 1. Modal está aberto
    // 2. Tem formato completo (8+ caracteres)
    // 3. Não está carregando
    // 4. É um código diferente do último buscado (evita loop infinito)
    // 5. Não está inicializando
    if (hasCompleteFormat && !loading && !isInitializingRef.current && code !== lastSearchedCodeRef.current) {
      // Limpar campo de pesquisa quando NFC detectar código (evita autofill)
      setProductSearch('');
      const productInput = document.querySelector('input[name="product-search-canteen"]');
      if (productInput) {
        productInput.value = '';
        productInput.blur(); // Remover foco para evitar autofill
      }
      
      const timer = setTimeout(() => {
        lastSearchedCodeRef.current = code;
        handleSearchStudent(code);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [studentCode, loading, handleSearchStudent, identifyModalOpen]);

  // Handler para quando NFC detectar código
  const handleNfcCodeDetected = (nfcCode) => {
    if (nfcCode && nfcCode.trim().length >= 4) {
      const normalizedCode = nfcCode.trim().toUpperCase();
      
      // LIMPAR campo de pesquisa imediatamente quando NFC detectar (evita autofill)
      setProductSearch('');
      const productInput = document.querySelector('input[name="product-search-canteen"]');
      if (productInput) {
        productInput.value = '';
        productInput.blur(); // Remover foco para evitar autofill
        // Prevenir eventos de autofill
        productInput.setAttribute('autocomplete', 'off');
        productInput.setAttribute('data-lpignore', 'true');
      }
      
      // Atualizar código real para busca (não atualizar modalCodeInput para não mostrar)
      setStudentCode(normalizedCode);
      // NÃO atualizar modalCodeInput - campo permanece vazio visualmente por segurança
      // A busca será feita automaticamente pelo useEffect
      handleSearchStudent(normalizedCode);
    }
  };

  const handleAddToCart = (product) => {
    // Verificar se produto tem estoque disponível
    const availableStock = product.availableStock || 0;
    if (availableStock <= 0) {
      setAlertState({
        show: true,
        type: 'error',
        message: `Produto "${product.name}" está fora de estoque`,
      });
      return;
    }

    setCart((prevCart) => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        if (existingItem.qty >= availableStock) {
          setAlertState({
            show: true,
            type: 'error',
            message: `Estoque insuficiente. Disponível: ${availableStock} unidades`,
          });
          return prevCart;
        }
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, qty: item.qty + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, qty: 1 }];
    });
  };

  const handleRemoveFromCart = (productId) => {
    const item = cart.find(item => item.id === productId);
    if (item && item.qty > 1) {
      setCart(cart.map(item =>
        item.id === productId
          ? { ...item, qty: item.qty - 1 }
          : item
      ));
    } else {
      setCart(cart.filter(item => item.id !== productId));
    }
  };

  const handleClearCart = () => {
    setCart([]);
    // Limpar também o estudante selecionado
    setStudentCode('');
    setStudentName('');
    setStudentBalance(null);
    lastSearchedCodeRef.current = '';
  };

  const calculateTotal = () => {
    const totalCents = cart.reduce(
      (total, item) => total + (toCents(item.priceCoins) * item.qty),
      0
    );
    return fromCents(totalCents);
  };

  const handleCheckout = async () => {
    if (!studentCode.trim()) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Busque um estudante ou colaborador primeiro',
      });
      return;
    }

    if (cart.length === 0) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Adicione produtos ao carrinho',
      });
      return;
    }

    try {
      setLoading(true);
      // Validar filial - OBRIGATÓRIA, sem fallback
      // Verificar estado atual do branch
      const currentBranch = branch || localStorage.getItem('canteen_filial') || null;
      
      if (!currentBranch || (currentBranch !== 'ALTO_BRANCO' && currentBranch !== 'CONCEICAO')) {
        setAlertState({
          show: true,
          type: 'error',
          message: `Filial inválida. Estado: ${branch}, Storage: ${localStorage.getItem('canteen_filial')}. Selecione uma filial válida.`,
        });
        setLoading(false);
        return;
      }

      const orderData = {
        cardCode: studentCode.trim().toUpperCase(), // Usar cardCode (aceita studentCode ou employeeCode)
        items: cart.map(item => ({
          productId: item.id,
          qty: item.qty,
        })),
        branch: currentBranch, // Usar filial validada
      };

      const result = await createOrder(orderData);
      
      // Mensagem diferente para estudantes (com saldo) e colaboradores (sem saldo)
      let successMessage = `Pedido realizado! Total: ${formatCurrency(result.totalCoins)}`;
      if (result.ownerType === 'STUDENT' && result.balanceAfter !== null) {
        successMessage += `. Saldo restante: ${formatCurrency(result.balanceAfter)}`;
      } else if (result.ownerType === 'EMPLOYEE') {
        successMessage += `. Compra registrada para desconto em folha.`;
      }
      
      setAlertState({
        show: true,
        type: 'success',
        message: successMessage,
      });

      // Limpar carrinho e atualizar saldo (apenas para estudantes)
      setCart([]);
      if (result.ownerType === 'STUDENT' && result.balanceAfter !== null) {
        setStudentBalance(result.balanceAfter);
      }
      
      // Recarregar produtos para atualizar estoque
      await loadProducts();
      
      // Limpar estado do cartão após finalizar venda para permitir ler outro cartão
      setStudentCode('');
      setStudentName('');
      setStudentBalance(null);
      setModalCodeInput('');
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const totalCoins = calculateTotal();
  
  // Converter balance para número para comparação correta
  let balanceNumber = null;
  if (studentBalance !== null && studentBalance !== undefined) {
    if (typeof studentBalance === 'string') {
      balanceNumber = parseFloat(studentBalance);
    } else {
      balanceNumber = Number(studentBalance);
    }
    // Se não for um número válido, considerar como null
    if (isNaN(balanceNumber) || !isFinite(balanceNumber)) {
      balanceNumber = null;
    }
  }
  
  // Para colaboradores, balanceNumber será null mas ainda podem fazer checkout
  const hasValidBalance = balanceNumber !== null;
  const balanceCents = hasValidBalance ? toCents(balanceNumber) : null;
  const totalCents = toCents(totalCoins);
  const hasSufficientBalance = hasValidBalance && balanceCents >= totalCents;
  const hasCard = studentCode.trim().length > 0; // Pode ser estudante ou colaborador
  const hasItems = cart.length > 0;
  // Permitir checkout se tiver código (estudante ou colaborador) e itens
  // Para estudantes, precisa ter saldo suficiente; para colaboradores, não precisa saldo
  const canCheckout = hasCard && hasItems && (hasValidBalance ? hasSufficientBalance : true);

  // Atalhos: F1 = tela cheia, F2 = modal cartão, F3 = finalizar, F5 = busca rápida de aluno
  useEffect(() => {
    const isInputFocused = () => {
      const el = document.activeElement;
      if (!el || !el.tagName) return false;
      const tag = el.tagName.toUpperCase();
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!el.isContentEditable;
    };

    const handleKeyDown = (e) => {
      if (e.key === 'F1') {
        e.preventDefault();
        handleToggleFullscreen();
        return;
      }
      if (e.key === 'F2') {
        e.preventDefault();
        if (!isInputFocused()) {
          openCardModal();
        }
        return;
      }
      if (e.key === 'F3') {
        e.preventDefault();
        if (!isInputFocused() && canCheckout && !loading) {
          handleCheckout();
        }
        return;
      }
      if (e.key === 'F5' && identifyModalOpenRef.current) {
        e.preventDefault();
        e.stopPropagation();
        openQuickSearch();
        return;
      }
      // ESC: fechar modal de NFC se aberto, senão limpar carrinho (não sai de tela cheia)
      if (e.key === 'Escape') {
        e.preventDefault();
        if (quickSearchOpen) {
          closeQuickSearch();
          return;
        }
        if (identifyModalOpenRef.current) {
          setIdentifyModalOpen(false);
          setModalCodeInput('');
          setQuickSearchOpen(false);
          setQuickSearchTerm('');
          setQuickSearchResults([]);
          setQuickSearchError('');
          lastSearchedCodeRef.current = '';
        } else {
          handleClearCart();
        }
        return;
      }
      // Teclas 1-9, 0 e a-z: adicionar produto pela posição (1º ao 36º) — usa ref para lista atual
      const currentList = filteredProductsRef.current;
      if (!isInputFocused() && currentList.length > 0) {
        const key = e.key.toLowerCase();
        let index = -1;
        if (key >= '1' && key <= '9') {
          index = parseInt(key, 10) - 1;
        } else if (key === '0') {
          index = 9;
        } else if (key >= 'a' && key <= 'z') {
          index = 10 + (key.charCodeAt(0) - 'a'.charCodeAt(0)); // a=10, b=11, ..., z=35
        }
        if (index >= 0 && index < currentList.length) {
          const product = currentList[index];
          const availableStock = product.availableStock || 0;
          const isOutOfStock = availableStock <= 0;
          const isDisabled = !product.isActive || isOutOfStock;
          if (!isDisabled) {
            e.preventDefault();
            handleAddToCart(product);
          }
        }
      }
    };

    // Usar capture: true para interceptar ESC antes do navegador (evitar sair de tela cheia)
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [canCheckout, loading, openCardModal, openQuickSearch, closeQuickSearch, quickSearchOpen]);

  // Manter refs atualizadas para os atalhos
  filteredProductsRef.current = filteredProducts;
  identifyModalOpenRef.current = identifyModalOpen;

  return (
    <Container ref={fullscreenRef} className={isFullscreen ? 'fullscreen-mode' : ''}>
      {!isFullscreen && <Header />}
      {!isFullscreen && <IconsBar />}
      <ContentArea>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, position: 'relative', zIndex: 10, backgroundColor: 'rgba(255,255,255,0.96)' }}>
          <PageTitle sx={{ margin: 0 }}>Cantina - PDV</PageTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }} required>
              <InputLabel id="filial-select-label">Filial</InputLabel>
              <Select
                labelId="filial-select-label"
                id="filial-select"
                value={branch}
                label="Filial"
                onChange={(e) => {
                  const newBranch = e.target.value;
                  
                  // Validar antes de salvar
                  if (newBranch === 'ALTO_BRANCO' || newBranch === 'CONCEICAO') {
                    setBranch(newBranch);
                    // Salvar no localStorage
                    try {
                      localStorage.setItem('canteen_filial', newBranch);
                    } catch (error) {
                      console.error('[Canteen] Erro ao salvar filial no localStorage:', error);
                    }
                    
                    // Limpar carrinho ao trocar filial
                    setCart([]);
                    setStudentCode('');
                    setStudentName('');
                    setStudentBalance(null);
                  } else {
                    console.error('[Canteen] Filial inválida:', newBranch, 'tipo:', typeof newBranch);
                  }
                }}
              >
                <MenuItem value="ALTO_BRANCO">Alto Branco</MenuItem>
                <MenuItem value="CONCEICAO">Conceição</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              onClick={handleToggleFullscreen}
              sx={{
                color: '#1e958c',
                backgroundColor: 'rgba(30, 149, 140, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(30, 149, 140, 0.2)',
                },
              }}
              title={isFullscreen ? 'Sair do modo tela cheia (F1)' : 'Entrar em modo tela cheia (F1)'}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Box>
        </Box>

        <PDVArea>
          {/* Área de produtos - PRIORIDADE MÁXIMA */}
          <ProductsArea>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#0b4f6c', fontWeight: 600 }}>
                  Produtos ({filteredProducts.length})
                </Typography>
                {products.length > 5 && (
                  <Chip
                    label={`${products.length} cadastrados`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                )}
              </Box>

              {products.length > 5 && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar produto..."
                  value={productSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Se o valor contém algo que parece ser nome de usuário (como "ivanildo"), limpar
                    // Isso previne autofill de preencher com dados de login
                    if (value && value.length > 0 && value.toLowerCase().includes('ivanildo')) {
                      e.target.value = '';
                      setProductSearch('');
                      return;
                    }
                    setProductSearch(value);
                  }}
                  onFocus={(e) => {
                    // Limpar se vier preenchido pelo autofill ao focar
                    if (e.target.value && e.target.value !== productSearch) {
                      e.target.value = '';
                      setProductSearch('');
                    }
                  }}
                  autoComplete="off"
                  name="product-search-canteen"
                  id="product-search-canteen-input"
                  type="search"
                  inputProps={{
                    autoComplete: 'off',
                    autoCapitalize: 'off',
                    autoCorrect: 'off',
                    spellCheck: 'false',
                    name: 'product-search-canteen',
                    id: 'product-search-canteen-input',
                    'data-lpignore': 'true',
                    'data-form-type': 'other',
                    'data-1p-ignore': 'true',
                    role: 'searchbox',
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                    endAdornment: productSearch ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setProductSearch('')}
                          edge="end"
                          sx={{ color: '#999' }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                    'data-lpignore': 'true',
                  }}
                  sx={{ mb: 2 }}
                />
              )}

              {productsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                  <CircularProgress />
                </Box>
              ) : filteredProducts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  {productSearch ? (
                    <>
                      <Typography color="textSecondary" sx={{ mb: 1 }}>
                        Nenhum produto encontrado para "{productSearch}"
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setProductSearch('')}
                        sx={{ color: '#1e958c' }}
                      >
                        Limpar busca
                      </Button>
                    </>
                  ) : (
                    <Typography color="textSecondary">Nenhum produto cadastrado</Typography>
                  )}
                </Box>
              ) : (
                <ProductGrid>
                  {filteredProducts.map((product, index) => {
                    const availableStock = product.availableStock || 0;
                    const isOutOfStock = availableStock <= 0;
                    const isDisabled = !product.isActive || isOutOfStock;
                    // Atalho: 1-9 e 0 para posições 0-9; a-z para posições 10-35 (até 36 itens com atalho)
                    const shortcutKey = index <= 9
                      ? (index < 9 ? String(index + 1) : '0')
                      : index <= 35
                        ? String.fromCharCode(97 + index - 10)
                        : null;
                    
                    return (
                      <ProductCard
                        key={product.id}
                        onClick={() => !isDisabled && handleAddToCart(product)}
                        disabled={isDisabled}
                        sx={{
                          opacity: isDisabled ? 0.5 : 1,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          position: 'relative',
                          '&:hover': isDisabled ? {
                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                            transform: 'none',
                            borderColor: 'transparent',
                          } : {},
                        }}
                      >
                        {shortcutKey && (
                          <Box
                            component="span"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: isDisabled ? 'grey.400' : '#1e958c',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px',
                              fontWeight: 700,
                              boxShadow: 2,
                              zIndex: 2,
                              pointerEvents: 'none',
                            }}
                            title={`Atalho: tecla ${shortcutKey}`}
                            aria-label={`Atalho tecla ${shortcutKey}`}
                          >
                            {shortcutKey}
                          </Box>
                        )}
                        <ProductName>{product.name}</ProductName>
                        <ProductPrice>{formatCurrency(product.priceCoins)}</ProductPrice>
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          {isOutOfStock ? (
                            <Chip
                              label="Sem estoque"
                              size="small"
                              color="error"
                              sx={{ fontSize: '10px', fontWeight: 600 }}
                            />
                          ) : (
                            <Chip
                              label={`Estoque: ${availableStock}`}
                              size="small"
                              color={availableStock <= 5 ? 'warning' : 'success'}
                              sx={{ fontSize: '10px', fontWeight: 600 }}
                            />
                          )}
                          {!product.isActive && (
                            <Chip
                              label="Inativo"
                              size="small"
                              color="default"
                              sx={{ fontSize: '10px' }}
                            />
                          )}
                        </Box>
                      </ProductCard>
                    );
                  })}
                </ProductGrid>
              )}
            </ProductsArea>

          {/* Área do carrinho */}
          <CartArea>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon sx={{ color: '#1e958c' }} />
                <Typography variant="h6" sx={{ color: '#0b4f6c', fontWeight: 600 }}>
                  Carrinho {cart.length > 0 && `(${cart.length})`}
                </Typography>
              </Box>
              {cart.length > 0 && (
                <Button
                  size="small"
                  onClick={handleClearCart}
                  sx={{ color: '#d32f2f', minWidth: 'auto', padding: '4px 8px' }}
                >
                  Limpar
                </Button>
              )}
            </Box>

            {cart.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                flex: 1,
                padding: 4,
                textAlign: 'center'
              }}>
                <ShoppingCartIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                <Typography color="textSecondary" variant="body2">
                  Carrinho vazio
                </Typography>
              </Box>
            ) : (
              <>
                <CartItems>
                  {cart.map((item) => {
                    const availableStock = item.availableStock || 0;
                    const canAddMore = availableStock > item.qty;
                    
                    return (
                      <CartItem key={item.id}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatCurrency(item.priceCoins)}/un
                            {availableStock > 0 && (
                              <span style={{ marginLeft: '8px', color: canAddMore ? '#2e7d32' : '#d32f2f' }}>
                                • Estoque: {availableStock}
                              </span>
                            )}
                          </Typography>
                        </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFromCart(item.id)}
                          sx={{ 
                            color: '#d32f2f',
                            padding: '4px'
                          }}
                        >
                          {item.qty > 1 ? <RemoveIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                        <Typography 
                          sx={{ 
                            minWidth: '24px', 
                            textAlign: 'center', 
                            fontWeight: 600,
                            fontSize: '14px'
                          }}
                        >
                          {item.qty}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleAddToCart(item)}
                          disabled={!canAddMore}
                          sx={{ 
                            color: '#1e958c',
                            padding: '4px'
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CartItem>
                    );
                  })}
                </CartItems>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <CartTotal>
                    <Typography sx={{ color: 'white', fontWeight: 600 }}>
                      Total
                    </Typography>
                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '20px' }}>
                      {formatCurrency(totalCoins)}
                    </Typography>
                  </CartTotal>

                  {/* Botão ou Info do Estudante */}
                  {!studentCode.trim() ? (
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<CreditCardIcon />}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openCardModal();
                      }}
                      sx={{
                        borderColor: '#1e958c',
                        color: '#1e958c',
                        '&:hover': {
                          borderColor: '#0b4f6c',
                          bgcolor: '#e3f2fd',
                        },
                      }}
                      title="Ler cartão (F2)"
                    >
                      LER CARTÃO
                    </Button>
                  ) : (
                    <Box sx={{ 
                      p: 1.5, 
                      bgcolor: '#e8f5e9', 
                      borderRadius: '8px', 
                      border: '1px solid #1e958c',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PersonIcon sx={{ color: '#1e958c', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0b4f6c' }}>
                          {studentName || 'Cartão identificado'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalanceWalletIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
                        {studentBalance !== null ? (
                          <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                            Saldo: {formatCurrency(studentBalance)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                            Colaborador (sem saldo)
                          </Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        onClick={() => {
                          setStudentCode('');
                          setStudentBalance(null);
                          setStudentName('');
                        }}
                        sx={{ mt: 1, color: '#d32f2f', fontSize: '11px' }}
                      >
                        Trocar cartão
                      </Button>
                    </Box>
                  )}

                  {balanceNumber !== null && !isNaN(balanceNumber) && (
                    <Box sx={{ 
                      padding: '8px 12px',
                      borderRadius: '6px',
                      bgcolor: balanceCents >= totalCents ? '#e8f5e9' : '#ffebee',
                      textAlign: 'center'
                    }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: balanceCents >= totalCents ? '#2e7d32' : '#d32f2f',
                          fontWeight: 600,
                          fontSize: '13px'
                        }}
                      >
                        Saldo após: {formatCurrency(fromCents(balanceCents - totalCents))}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleCheckout}
                    disabled={!canCheckout || loading || (hasValidBalance && !hasSufficientBalance)}
                    title="Finalizar pedido (F3)"
                    sx={{
                      bgcolor: '#1e958c',
                      padding: 1.5,
                      fontSize: '16px',
                      fontWeight: 600,
                      mt: 1,
                      '&:hover': { bgcolor: '#0b4f6c' },
                      '&:disabled': {
                        bgcolor: '#ccc',
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Finalizar Pedido'}
                  </Button>

                  {balanceNumber !== null && !isNaN(balanceNumber) && balanceCents < totalCents && (
                    <Alert severity="error" sx={{ mt: 1, fontSize: '12px' }}>
                      Saldo insuficiente. Saldo disponível: {formatCurrency(balanceNumber)}
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </CartArea>
        </PDVArea>
      </ContentArea>

      {/* Modal de Identificação do Estudante */}
      <Dialog 
        open={identifyModalOpen} 
        onClose={() => {
          // Limpar apenas o campo mascarado do modal ao fechar manualmente
          // NÃO limpar studentCode, studentBalance, studentName - manter dados do estudante
          setModalCodeInput('');
          setQuickSearchOpen(false);
          setQuickSearchTerm('');
          setQuickSearchResults([]);
          setQuickSearchError('');
          // Limpar referência para permitir nova tentativa quando modal reabrir
          lastSearchedCodeRef.current = '';
          setIdentifyModalOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        container={isFullscreen ? fullscreenRef.current : undefined}
        sx={{
          zIndex: isFullscreen ? 10000 : undefined, // z-index muito alto quando em fullscreen
          '& .MuiBackdrop-root': {
            zIndex: isFullscreen ? 9999 : undefined, // backdrop também precisa de z-index alto
            backgroundColor: isFullscreen ? 'rgba(0, 0, 0, 0.75)' : undefined, // backdrop mais escuro em fullscreen
          },
          '& .MuiDialog-container': {
            zIndex: isFullscreen ? 10000 : undefined,
          },
          '& .MuiDialog-paper': {
            zIndex: isFullscreen ? 10001 : undefined, // papel do dialog acima do backdrop
            position: isFullscreen ? 'relative' : undefined,
          },
        }}
        PaperProps={{
          component: 'div',
          'data-lpignore': 'true',
          'data-form-type': 'other',
          sx: {
            zIndex: isFullscreen ? 10001 : undefined,
            position: isFullscreen ? 'relative' : undefined,
          },
        }}
      >
        {/* Campo fake hidden para confundir gerenciadores de senha */}
        <input
          type="text"
          name="fake-username"
          autoComplete="username"
          style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
          tabIndex={-1}
          readOnly
          value=""
        />
        <input
          type="password"
          name="fake-password"
          autoComplete="current-password"
          style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
          tabIndex={-1}
          readOnly
          value=""
        />
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: '#e2f4f2',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <PersonIcon sx={{ color: '#087f78' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#123b4a', fontWeight: 700, lineHeight: 1.25 }}>
              Identificar pessoa
            </Typography>
            <Typography variant="body2" sx={{ color: '#607d8b', mt: 0.25 }}>
              Busque pelo nome ou leia o cartão NFC
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box 
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
            component="div"
            data-lpignore="true"
            data-form-type="other"
          >
            <Box
              sx={{
                order: 2,
                p: 2,
                bgcolor: nfcReading ? '#e3f4f7' : '#f7fafb',
                borderRadius: 2,
                border: `1px solid ${nfcReading ? '#1e958c' : '#d7e3e7'}`,
                transition: 'all 0.3s',
              }}
            >
              {nfcReading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CircularProgress size={34} sx={{ color: '#1e958c' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: '#0b4f6c', fontWeight: 700 }}>
                      Lendo cartão NFC...
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#607d8b' }}>
                      Aguarde a identificação
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: 2,
                      bgcolor: '#eaf3f4',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <CreditCardIcon sx={{ color: '#4e7782', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: '#294d59', fontWeight: 700 }}>
                      Usar cartão NFC
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#607d8b' }}>
                      Aproxime o cartão do leitor ou digite o código abaixo
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <TextField
              key={`student-code-input-${identifyModalOpen ? 'open' : 'closed'}`}
              label="Código do Cartão"
              type="text"
              value={modalCodeInput || ''}
              inputRef={inputRef}
              onChange={(e) => {
                // BLOQUEAR eventos durante inicialização
                if (isInitializingRef.current) {
                  // Limpar apenas o campo mascarado se vier preenchido durante inicialização
                  // NÃO limpar studentCode - manter se já existir
                  e.target.value = '';
                  setModalCodeInput('');
                  return;
                }
                
                const value = e.target.value.toUpperCase();
                
                // Ignorar se o modal não estiver aberto (proteção extra)
                if (!identifyModalOpen) {
                  return;
                }
                
                // Se o código mudou, limpar referência para permitir nova busca
                // Isso permite tentar novamente quando o usuário digita um código diferente
                const previousCode = studentCode.trim();
                if (value.trim() !== previousCode) {
                  lastSearchedCodeRef.current = '';
                }
                
                // Atualizar o campo mascarado (mostra asteriscos)
                setModalCodeInput(value);
                // Atualizar o código real (sem mascarar) para busca
                setStudentCode(value);
                
                // Limpar timer anterior se existir
                if (searchTimerRef.current) {
                  clearTimeout(searchTimerRef.current);
                }
                
                // Buscar automaticamente quando tiver 8+ caracteres (com ou sem hífen)
                const trimmedValue = value.trim();
                if (trimmedValue.length >= 8) {
                  // Usar debounce para evitar múltiplas chamadas durante digitação
                  searchTimerRef.current = setTimeout(() => {
                    // Verificar novamente se o modal ainda está aberto, não está inicializando e é um código diferente
                    if (!isInitializingRef.current && identifyModalOpen && trimmedValue.length >= 8 && trimmedValue !== lastSearchedCodeRef.current) {
                      handleSearchStudent(trimmedValue);
                    }
                  }, 500);
                }
              }}
              onFocus={(e) => {
                // Se estiver inicializando, garantir que o campo mascarado esteja vazio
                // NÃO limpar studentCode - manter se já existir
                if (isInitializingRef.current) {
                  if (e.target) {
                    e.target.value = '';
                  }
                  if (inputRef.current) {
                    inputRef.current.value = '';
                  }
                  setModalCodeInput('');
                  // NÃO limpar studentCode aqui
                }
              }}
              onBlur={() => {
                // Limpar timer ao sair do campo
                if (searchTimerRef.current) {
                  clearTimeout(searchTimerRef.current);
                  searchTimerRef.current = null;
                }
                // Buscar quando sair do campo se tiver código válido e for diferente do último buscado
                const code = modalCodeInput.trim() || studentCode.trim();
                if (code.length >= 8 && code !== lastSearchedCodeRef.current && !isInitializingRef.current) {
                  handleSearchStudent(code);
                }
              }}
              onKeyDown={(e) => {
                // BLOQUEAR eventos durante inicialização
                if (isInitializingRef.current) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                
                // Prevenir Enter de disparar busca se o campo estiver vazio
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Verificar se o modal está realmente aberto
                  if (!identifyModalOpen) {
                    return;
                  }
                  
                  const code = modalCodeInput.trim() || studentCode.trim();
                  
                  // Só buscar se tiver código válido E não estiver já buscando
                  if (code.length >= 8 && code !== lastSearchedCodeRef.current && !loading && !isInitializingRef.current) {
                    if (searchTimerRef.current) {
                      clearTimeout(searchTimerRef.current);
                      searchTimerRef.current = null;
                    }
                    handleSearchStudent(code);
                  }
                }
              }}
              onKeyPress={(e) => {
                // BLOQUEAR eventos durante inicialização
                if (isInitializingRef.current) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                
                // Prevenir qualquer evento de teclado indesejado
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              fullWidth
              variant="outlined"
              placeholder="Aproxime o cartão ou digite o código"
              autoFocus={true}
              name="student-code-nfc-input"
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                autoCapitalize: 'off',
                autoCorrect: 'off',
                spellCheck: 'false',
                'data-lpignore': 'true',
                'data-form-type': 'other',
                'data-1p-ignore': 'true',
                role: 'textbox',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CreditCardIcon sx={{ color: '#1e958c' }} />
                  </InputAdornment>
                ),
                'data-lpignore': 'true',
              }}
              sx={{
                order: 3,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1e958c',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1e958c',
                    borderWidth: 2,
                  },
                },
              }}
              helperText="O código será lido automaticamente ao aproximar o cartão e não será exibido por segurança"
            />

            <Button
              variant={quickSearchOpen ? 'text' : 'contained'}
              startIcon={<SearchIcon />}
              onClick={quickSearchOpen ? closeQuickSearch : openQuickSearch}
              sx={{
                order: 0,
                alignSelf: 'stretch',
                minHeight: 48,
                bgcolor: quickSearchOpen ? 'transparent' : '#087f78',
                color: quickSearchOpen ? '#0b4f6c' : '#fff',
                fontWeight: 600,
                boxShadow: quickSearchOpen ? 'none' : '0 4px 12px rgba(8, 127, 120, 0.2)',
                '&:hover': {
                  bgcolor: quickSearchOpen ? '#f1faf9' : '#086a65',
                  boxShadow: quickSearchOpen ? 'none' : '0 5px 14px rgba(8, 127, 120, 0.25)',
                },
              }}
            >
              {quickSearchOpen ? 'Fechar busca' : 'Buscar por nome (F5)'}
            </Button>

            {quickSearchOpen && (
              <Box
                sx={{
                  order: 1,
                  p: 2,
                  border: '1px solid #b7ddd9',
                  borderRadius: 2,
                  bgcolor: '#f8fcfc',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#0b4f6c', fontWeight: 700 }}>
                  Busca rápida de aluno ou colaborador
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    inputRef={quickSearchInputRef}
                    value={quickSearchTerm}
                    onChange={(event) => {
                      const value = event.target.value;
                      setQuickSearchTerm(value);
                      setQuickSearchError('');
                      scheduleQuickPersonSearch(value);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        moveQuickSearchHighlight(1);
                        return;
                      }
                      if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        moveQuickSearchHighlight(-1);
                        return;
                      }
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        const highlightedPerson = quickSearchResults[quickSearchHighlightedIndex];
                        if (highlightedPerson) {
                          handleSelectQuickSearchPerson(highlightedPerson);
                        } else if (quickSearchTerm.trim().length >= 3) {
                          if (quickSearchTimerRef.current) {
                            clearTimeout(quickSearchTimerRef.current);
                          }
                          handleQuickPersonSearch(quickSearchTerm);
                        }
                      }
                    }}
                    label="Nome"
                    placeholder="Digite 3 letras ou mais"
                    size="small"
                    fullWidth
                    autoComplete="off"
                    helperText="A busca é automática. Use ↑ e ↓ para escolher e Enter para confirmar."
                    inputProps={{
                      'data-lpignore': 'true',
                      'data-1p-ignore': 'true',
                      'aria-activedescendant': quickSearchHighlightedIndex >= 0
                        ? `quick-search-result-${quickSearchHighlightedIndex}`
                        : undefined,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#1e958c' }} />
                        </InputAdornment>
                      ),
                      endAdornment: quickSearchLoading ? <CircularProgress size={20} /> : null,
                    }}
                  />
                </Box>

                {quickSearchError && (
                  <Alert severity={quickSearchResults.length ? 'warning' : 'info'} sx={{ mt: 1.5, py: 0 }}>
                    {quickSearchError}
                  </Alert>
                )}

                {quickSearchResults.length > 0 && (
                  <Box
                    role="listbox"
                    sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 260, overflowY: 'auto' }}
                  >
                    {quickSearchResults.map((person, index) => (
                      <Button
                        key={`${person.ownerType}-${person.id}`}
                        id={`quick-search-result-${index}`}
                        data-quick-search-index={index}
                        role="option"
                        aria-selected={quickSearchHighlightedIndex === index}
                        variant="text"
                        onClick={() => handleSelectQuickSearchPerson(person)}
                        onMouseEnter={() => setQuickSearchHighlightedIndex(index)}
                        sx={{
                          justifyContent: 'space-between',
                          textTransform: 'none',
                          textAlign: 'left',
                          px: 1.5,
                          py: 1,
                          bgcolor: quickSearchHighlightedIndex === index ? '#dff3f0' : '#fff',
                          border: `1px solid ${quickSearchHighlightedIndex === index ? '#1e958c' : '#dcebea'}`,
                          color: '#263238',
                          '&:hover': { bgcolor: '#e8f5f3', borderColor: '#1e958c' },
                        }}
                      >
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                          {person.name}
                        </Typography>
                        <Chip
                          component="span"
                          label={person.ownerType === 'EMPLOYEE' ? 'Colaborador' : 'Aluno'}
                          size="small"
                          color={person.ownerType === 'EMPLOYEE' ? 'info' : 'success'}
                          variant="outlined"
                          sx={{ ml: 2 }}
                        />
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {studentCode.trim().length > 0 && (
              <Box sx={{ 
                order: 4,
                p: 2, 
                bgcolor: '#e8f5e9', 
                borderRadius: '8px', 
                border: '2px solid #1e958c',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PersonIcon sx={{ color: '#1e958c', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#0b4f6c' }}>
                    {studentName || 'Cartão identificado'}
                  </Typography>
                </Box>
                {studentBalance !== null ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalanceWalletIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                      <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                        Saldo: {formatCurrency(studentBalance)}
                      </Typography>
                    </Box>
                    <Chip
                      label="Estudante Ativo"
                      color="success"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </>
                ) : (
                  <Chip
                    label="Colaborador Ativo"
                    color="info"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            // Limpar apenas o campo mascarado do modal ao fechar manualmente
            // NÃO limpar studentCode, studentBalance, studentName - manter dados do estudante
            setModalCodeInput('');
            setQuickSearchOpen(false);
            setQuickSearchTerm('');
            setQuickSearchResults([]);
            setQuickSearchError('');
            // Limpar referência para permitir nova tentativa quando modal reabrir
            lastSearchedCodeRef.current = '';
            setIdentifyModalOpen(false);
          }}>
            {studentBalance !== null ? 'Fechar' : 'Cancelar'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </Container>
  );
};

export default Canteen;
